/**
 * Scene id to the thing that draws it.
 *
 * The other half of `talk/demos.ts`, which holds the ids, who may show them
 * and how the offline path matches them - everything the brief, the wire and
 * the check harness need, and none of which should drag three.js in to get
 * it. This file is the half that knows about React, and only the chat panel
 * imports it.
 *
 * EVERY ENTRY IS A DYNAMIC IMPORT, for the reason `tools/registry.ts` gives
 * for the panels: a molecular-dynamics viewport and an Energy2D solver are
 * not small, and a student who never asks a technician how anything works
 * should not download eleven of them. The map itself is tiny; each value is
 * a chunk that arrives when a scene is actually named.
 *
 * `npm run talk` asserts these keys and the registry's ids are the same set,
 * so a scene the model can name always has something to draw.
 */

import type { ComponentType, ReactNode } from 'react'

/**
 * The two scenes that are a graph and nothing else, held to a readable
 * width and centred in the card.
 *
 * Every other scene fills the card because it is a viewer with its graph
 * beside it. These two have no viewer to sit beside, and left alone in a
 * card as wide as the panel they went wrong in opposite ways: the band plot
 * is a fixed 430 px canvas, so it hugged the left edge with half the card
 * empty beside it, and the carrier chart follows its container, so it drew
 * itself at twice its logical size with axis labels to match. Centred at
 * the width the chart column of every other scene uses, they read as the
 * same instrument the rest of the deck is made of.
 *
 * Same argument SheathDemo makes for the one field scene that carries no
 * sensors: a canvas pinned to the left of a card twice its width reads as
 * something that failed to load.
 */
const CHART_ONLY_W = 430
const ChartOnly = ({ children }: { children: ReactNode }) => (
  <div style={{ maxWidth: CHART_ONLY_W, margin: '0 auto' }}>{children}</div>
)

/**
 * Every view that has a Run button is mounted with `embedded`, which is what
 * tells it this is a conversation and not its panel: it starts itself, and it
 * leaves the panel's second experiment and its authored prose behind. The two
 * that solve on mount and have nothing to run - the band structure and the
 * carrier curve - need no such flag.
 *
 * Views that take props get a zero-prop wrapper here rather than in the
 * component: the panels pass their own values and are entitled to, and a
 * conversation has no store to read one from. The choice each wrapper makes
 * is the ordinary case - n-type silicon, a silicon band structure.
 */
/**
 * What a scene component may be told. `asked` is the student's question,
 * for the one scene with two faces (tunnelling: the 2D box or the 3D
 * volume) - every other view ignores it.
 */
export interface DemoProps {
  asked?: string
}

export const DEMO_VIEWS: Record<string, () => Promise<ComponentType<DemoProps>>> = {
  'crystal-lattice': () =>
    import('../../tools/CrystalLatticeView').then((m) => {
      const View = m.CrystalLatticeView
      return () => <View embedded />
    }),
  bands: () =>
    import('../../tools/BandStructureView').then((m) => {
      const View = m.BandStructureView
      return () => (
        <ChartOnly>
          <View crystal="Si" />
        </ChartOnly>
      )
    }),
  carriers: () =>
    import('../../tools/CarrierRegimeView').then((m) => {
      const View = m.CarrierRegimeView
      // Donors: phosphorus in silicon is the doping a student meets first,
      // and the freeze-out knee is the same shape either way.
      return () => (
        <ChartOnly>
          <View donor />
        </ChartOnly>
      )
    }),
  'si-surface': () =>
    import('../../tools/Si100SurfaceView').then((m) => {
      const View = m.Si100SurfaceView
      return () => <View embedded />
    }),
  'diffusion-hop': () =>
    import('../../tools/DiffusionHopView').then((m) => {
      const View = m.DiffusionHopView
      return () => <View embedded />
    }),
  'film-growth': () =>
    import('../../tools/CvdMdView').then((m) => {
      const View = m.CvdMdView
      return () => <View embedded />
    }),
  // Sarah's: the level diagram, the spectrum and the endpoint traces. With
  // no etcher behind it the view plays its own stand-in run, so it mounts
  // bare and starts itself.
  'plasma-emission': () =>
    import('../../tools/EmissionView').then((m) => {
      const View = m.EmissionView
      return () => <View embedded />
    }),
  'dopant-atom': () =>
    import('../../tools/DopantAtomView').then((m) => {
      const View = m.DopantAtomView
      return () => <View donor embedded />
    }),
  'photon-counting': () =>
    import('../../tools/PhotonCountView').then((m) => {
      const View = m.PhotonCountView
      return () => <View embedded />
    }),
  'channel-quantisation': () =>
    import('../../tools/InversionView').then((m) => {
      const View = m.InversionView
      return () => <View embedded />
    }),
  'raman-strain': () =>
    import('../../tools/PhononView').then((m) => {
      const View = m.PhononView
      return () => <View />
    }),
  // A band diagram on its side: a chart and nothing else.
  'quantum-well': () =>
    import('../../tools/QuantumWellView').then((m) => {
      const View = m.QuantumWellView
      return () => (
        <ChartOnly>
          <View />
        </ChartOnly>
      )
    }),
  // A spectrum and nothing else, so it takes the chart column's width.
  'resist-absorption': () =>
    import('../../tools/ResistAbsorptionView').then((m) => {
      const View = m.ResistAbsorptionView
      return () => (
        <ChartOnly>
          <View />
        </ChartOnly>
      )
    }),
  // The reactive half of the same tool: bonds that form and break. Its own
  // chunk, because the ReaxFF kernel is not small and only a student who
  // asks about a reaction should fetch it.
  chemisorption: () =>
    import('../../tools/CvdReactiveView').then((m) => {
      const View = m.CvdReactiveView
      return () => <View embedded />
    }),
  'electron-diffraction': () =>
    import('../../tools/DiffractionView').then((m) => {
      const View = m.DiffractionView
      return () => <View embedded />
    }),
  // A spectrum and nothing else, so it takes the chart column's width.
  'light-absorption': () =>
    import('../../tools/AbsorptionView').then((m) => {
      const View = m.AbsorptionView
      return () => (
        <ChartOnly>
          <View />
        </ChartOnly>
      )
    }),
  // A Schrödinger packet in a 2D box, not molecular dynamics: its own small
  // chunk, with a chip that swaps in the 3D volume (a worker run). The 1D
  // instrument (tools/TunnelView) is panel-only; see demos.ts.
  tunnelling: () => import('./TunnellingDemo').then((m) => m.TunnellingDemo),
  // Boron, matching the channelling scene's recipe, so the two things
  // Nicole can show are the same implant seen at two scales: where the
  // ions STOPPED, and what the atom that stopped there is now doing.
  'doped-lattice': () =>
    import('../../tools/ImplantLatticeView').then((m) => {
      const View = m.ImplantLatticeView
      return () => <View species="boron" embedded />
    }),
  // Its own chunk, not demoScenes': that module drags Energy2D and the
  // transport views, and this scene wants the implant Monte Carlo instead.
  channelling: () => import('./ChannellingDemo').then((m) => m.ChannellingDemo),
  sheath: () => import('./demoScenes').then((m) => m.SheathDemo),
  'trench-arde': () => import('./demoScenes').then((m) => m.TrenchDemo),
  'thermal-budget': () => import('./demoScenes').then((m) => m.ThermalBudgetDemo),
  'drive-in': () => import('./demoScenes').then((m) => m.DriveInDemo),
  'bay-airflow': () => import('./demoScenes').then((m) => m.BayAirflowDemo),
  // The line runs in its own worker and draws its own charts; the card just
  // hosts the same view the top-right Line button opens.
  'line-chiller': () => import('../ui/LineView').then((m) => m.LineView),
}

export const loadDemoView = (id: string): Promise<ComponentType<DemoProps>> | null =>
  DEMO_VIEWS[id]?.() ?? null
